export interface InterviewPlanWarningInterviewer {
  name?: string
  focusArea?: string
  customFocusArea?: string
  trainedByYogen?: boolean
  isBackup?: boolean
}

export interface InterviewPlanWarningStage {
  id: string
  name?: string
  goal?: string
  type?: 'phone' | 'video' | 'onsite'
  duration?: number
  daysToNext?: number
  useAiTools?: boolean
  stageFocusArea?: string | string[]
  interviewers?: InterviewPlanWarningInterviewer[]
}

const FOCUS_AREA_LABELS: Record<string, string> = {
  'technical-interview': 'Technical Skills',
  'technical-coding': 'Technical Coding',
  'system-design': 'System Design',
  'cultural-fit': 'Cultural Fit',
  behavioral: 'Behavioral Skills',
  leadership: 'Leadership Skills',
  communication: 'Communication Skills',
  'initial-screening': 'Initial Screening',
  executive: 'Executive',
}

function normalize(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
}

function focusAreaLabel(value: string): string {
  return FOCUS_AREA_LABELS[value] || value
}

function interviewerFocusArea(
  interviewer: InterviewPlanWarningInterviewer,
): string {
  const focusArea = normalize(interviewer.focusArea)
  if (focusArea === 'other') return normalize(interviewer.customFocusArea)
  return focusArea
}

function stageFocusAreas(stage: InterviewPlanWarningStage): string[] {
  const values = Array.isArray(stage.stageFocusArea)
    ? stage.stageFocusArea
    : [stage.stageFocusArea]

  return [
    ...new Set(
      values.map(normalize).filter((value) => value && value !== 'other'),
    ),
  ]
}

function stageLabel(stage: InterviewPlanWarningStage, index: number): string {
  const name = stage.name?.trim()
  return name ? `Stage ${index + 1} (${name})` : `Stage ${index + 1}`
}

/**
 * Analyze a complete interview plan so warnings can account for relationships
 * between stages. The returned map contains display-ready warnings keyed by
 * stage id; warnings are advisory and never block saving the plan.
 */
export function analyzeInterviewPlanWarnings(
  stages: InterviewPlanWarningStage[],
): Record<string, string[]> {
  const warningsByStage = Object.fromEntries(
    stages.map((stage) => [stage.id, [] as string[]]),
  )

  stages.forEach((stage, stageIndex) => {
    const warnings = warningsByStage[stage.id]
    const interviewers = stage.interviewers ?? []
    const requiredInterviewers = interviewers.filter(
      (interviewer) => !interviewer.isBackup,
    )
    const backupInterviewers = interviewers.filter(
      (interviewer) => interviewer.isBackup,
    )

    if (requiredInterviewers.length === 0) {
      warnings.push('No interviewers assigned.')
    }

    if (!stage.goal?.trim()) {
      warnings.push('No goal defined for this stage.')
    }

    const unnamedRequiredCount = requiredInterviewers.filter(
      (interviewer) => !interviewer.name?.trim(),
    ).length
    if (unnamedRequiredCount > 0) {
      warnings.push(
        `${unnamedRequiredCount} required interviewer${unnamedRequiredCount === 1 ? '' : 's'} need${unnamedRequiredCount === 1 ? 's' : ''} a name.`,
      )
    }

    const unnamedBackupCount = backupInterviewers.filter(
      (interviewer) => !interviewer.name?.trim(),
    ).length
    if (unnamedBackupCount > 0) {
      warnings.push(
        `${unnamedBackupCount} backup interviewer${unnamedBackupCount === 1 ? '' : 's'} need${unnamedBackupCount === 1 ? 's' : ''} a name.`,
      )
    }

    if (stage.type === 'onsite' && requiredInterviewers.length < 2) {
      warnings.push(
        'On-site interviews typically benefit from multiple interviewers.',
      )
    }

    if (requiredInterviewers.length >= 3) {
      warnings.push(
        `Scheduling complexity: This stage has ${requiredInterviewers.length} required interviewers. Confirm that each interviewer collects distinct evidence.`,
      )
    }

    if (requiredInterviewers.length > 0 && backupInterviewers.length === 0) {
      warnings.push(
        'Scheduling continuity: No backup interviewer is assigned. Consider adding one to prevent avoidable delays.',
      )
    }

    const untrainedInterviewers = interviewers.filter(
      (interviewer) => interviewer.trainedByYogen === false,
    )
    if (untrainedInterviewers.length > 0) {
      const names = untrainedInterviewers
        .map((interviewer) => interviewer.name?.trim() || 'Unnamed')
        .join(', ')
      warnings.push(
        `Untrained interviewer(s): ${names}. Ensure they complete training with Yogen.`,
      )
    }

    const interviewersByFocus = new Map<
      string,
      InterviewPlanWarningInterviewer[]
    >()
    requiredInterviewers.forEach((interviewer) => {
      const focusArea = interviewerFocusArea(interviewer)
      if (!focusArea) return
      const matchingInterviewers = interviewersByFocus.get(focusArea) ?? []
      matchingInterviewers.push(interviewer)
      interviewersByFocus.set(focusArea, matchingInterviewers)
    })

    interviewersByFocus.forEach((matchingInterviewers, focusArea) => {
      if (matchingInterviewers.length < 2) return
      warnings.push(
        `Focus overlap: ${matchingInterviewers.length} interviewers in this stage are assigned to ${focusAreaLabel(focusArea)}. Give each interviewer a distinct responsibility or confirm that both perspectives are necessary.`,
      )
    })

    const isFinalStage = stageIndex === stages.length - 1
    const daysToNext = Number(stage.daysToNext) || 0
    const duration = Number(stage.duration) || 0
    if (!isFinalStage && duration <= 30 && daysToNext >= 7) {
      warnings.push(
        `Disproportionate delay: This ${duration || 0}-minute stage adds ${daysToNext} days to the candidate journey. Consider grouping it with another interview day or removing it if it does not collect unique evidence.`,
      )
    } else if (!isFinalStage && daysToNext >= 14) {
      warnings.push(
        `Long gap: This stage adds ${daysToNext} days to the candidate journey, increasing delay and candidate drop-off risk.`,
      )
    } else if (!isFinalStage && daysToNext >= 8) {
      warnings.push(
        `Candidate-experience risk: This stage adds ${daysToNext} days before the next stage. Aim for seven days or fewer when possible.`,
      )
    }
  })

  const stagesByFocus = new Map<string, number[]>()
  stages.forEach((stage, stageIndex) => {
    stageFocusAreas(stage).forEach((focusArea) => {
      const matchingStages = stagesByFocus.get(focusArea) ?? []
      matchingStages.push(stageIndex)
      stagesByFocus.set(focusArea, matchingStages)
    })
  })

  stagesByFocus.forEach((matchingStageIndexes, focusArea) => {
    if (matchingStageIndexes.length < 2) return

    matchingStageIndexes.forEach((stageIndex) => {
      const stage = stages[stageIndex]
      const otherStages = matchingStageIndexes
        .filter((otherIndex) => otherIndex !== stageIndex)
        .map((otherIndex) => stageLabel(stages[otherIndex], otherIndex))
        .join(', ')

      warningsByStage[stage.id].push(
        `Possible duplicate evaluation: ${focusAreaLabel(focusArea)} is also assessed in ${otherStages}. Confirm that each stage collects distinct evidence or consider combining them.`,
      )
    })
  })

  return warningsByStage
}
